import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/admin/DashboardLayout';
import { Image, Users, FileText, Scissors, Shirt, Ruler, Quote, UserPlus, ChevronRight, Clock, Briefcase } from 'lucide-react';

const Dashboard = () => {
  const [stats, setStats] = useState({
    gallery: 0,
    portfolio: 0,
    articles: 0,
    applicants: 0,
    loker_active: 0
  });
  const [recentApplicants, setRecentApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const [greeting, setGreeting] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/dashboard_stats.php`);
        setStats(response.data);
      } catch (error) {
        console.error("Failed to fetch stats", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchRecentApplicants = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/all_applicants.php`);
        if (Array.isArray(response.data)) {
            // Ambil 3 pelamar terbaru
            setRecentApplicants(response.data.slice(0, 3));
        }
      } catch (error) {
        console.error("Failed to fetch recent applicants", error);
      }
    };

    const fetchQuotes = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/quotes.php`);
            if (Array.isArray(res.data) && res.data.length > 0) {
                setQuotes(res.data);
            }
        } catch (err) {
            console.error("Failed quotes", err);
        }
    };

    fetchStats();
    fetchRecentApplicants();
    fetchQuotes();

    // Set Greeting
    const hour = new Date().getHours();
    if (hour < 11) setGreeting('Pagi');
    else if (hour < 15) setGreeting('Siang');
    else if (hour < 19) setGreeting('Sore');
    else setGreeting('Malam');

  }, []);

  // Quotes Slider
  useEffect(() => {
      if (quotes.length === 0) return;
      const interval = setInterval(() => {
          setCurrentQuoteIndex((prev) => (prev + 1) % quotes.length);
      }, 15000); // Change every 15 seconds
      return () => clearInterval(interval);
  }, [quotes]);

  const styles = {
    heroWrapper: {
        position: 'relative',
        marginBottom: '2rem',
        marginTop: '-2rem',
        marginLeft: '-2rem',
        marginRight: '-2rem',
        paddingBottom: '10rem',
        borderRadius: '0 0 3rem 3rem',
        overflow: 'hidden',
        
        // PERFORMANCE FIX: Ganti pattern berat dengan Gradient Glow yang ringan
        backgroundColor: '#0f172a', // Slate-900 (Base Dark)
        backgroundImage: `
            radial-gradient(circle at 15% 50%, rgba(99, 102, 241, 0.15) 0%, transparent 25%), 
            radial-gradient(circle at 85% 30%, rgba(14, 165, 233, 0.15) 0%, transparent 25%)
        `, // Soft Indigo & Sky Blue glow spots (Ambient Light)
        
        // Fading tetep dipake, tapi sekarang bakal smooth karena backgroundnya ringan
        maskImage: 'linear-gradient(to bottom, #000 60%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to bottom, #000 60%, transparent 100%)',
    },
    heroContent: {
        position: 'relative',
        zIndex: 10,
        padding: 'clamp(4rem, 10vh, 6rem) 2rem clamp(3rem, 8vh, 5rem) 2rem', 
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        fontFamily: '"Public Sans", sans-serif',
    },
    // Icons: Putih transparan biar masuk sama background gelap
    bgIcon1: { position: 'absolute', top: '12%', left: '6%', opacity: 0.05, transform: 'rotate(-10deg) scale(1.2)', color: '#fff' },
    bgIcon2: { position: 'absolute', bottom: '30%', right: '6%', opacity: 0.05, transform: 'rotate(10deg) scale(1.4)', color: '#fff' },
    bgIcon3: { position: 'absolute', top: '20%', right: '20%', opacity: 0.03, transform: 'rotate(25deg)', color: '#fff' },

    greeting: {
        fontSize: 'clamp(2rem, 4vw, 3.5rem)',
        fontWeight: '700',
        color: '#f8fafc',
        marginBottom: '2rem',
        letterSpacing: '-0.02em',
        lineHeight: '1.2',
        textShadow: '0 4px 20px rgba(0,0,0,0.5)' // Shadow lebih deep dikit
    },
    quoteContainer: {
        maxWidth: '750px',
        width: '100%',
        position: 'relative',
        padding: '0 1rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1rem',
        // Anti-Jitter: Kunci tinggi container biar gak gerak pas ganti quote
        minHeight: '160px', 
        justifyContent: 'center',
    },
    quoteIcon: {
        display: 'block',
        color: '#94a3b8', // Slate-400 (Muted Blue-Grey)
        opacity: 0.6,
        width: '2rem',
        height: '2rem'
    },
    quoteText: {
        fontSize: 'clamp(1.1rem, 2vw, 1.35rem)',
        lineHeight: '1.6',
        color: '#cbd5e1', // Slate-300 (Soft White)
        fontWeight: '300', // Light font weight biar modern & clean
        fontStyle: 'normal', // Gak usah italic biar lebih "Swiss Style" / Modern
        fontFamily: '"Public Sans", sans-serif',
        maxWidth: '80%',
        letterSpacing: '0.01em'
    },
    
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
      gap: '1.5rem',
      marginBottom: '2rem',
      padding: '0 1rem'
    },
    card: {
      backgroundColor: '#fff',
      padding: '1.5rem',
      borderRadius: '1rem',
      border: '1px solid #e5e7eb',
      display: 'flex',
      alignItems: 'center',
      gap: '1.5rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
      transition: 'transform 0.2s, box-shadow 0.2s',
      cursor: 'pointer',
      textDecoration: 'none',
      color: 'inherit'
    },
    iconBox: (color) => ({
      width: '3rem',
      height: '3rem',
      borderRadius: '0.75rem',
      backgroundColor: color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff'
    }),
    label: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginBottom: '0.25rem',
      fontWeight: '500'
    },
    value: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#111'
    },
    sectionTitle: {
        fontSize: '1.25rem',
        fontWeight: '700',
        marginBottom: '1rem',
        padding: '0 1rem',
        color: '#1f2937'
    }
  };

  const statCards = [
    { label: 'Total Galeri', value: stats.gallery, icon: <Image size={24} />, color: '#3b82f6', link: '/admin/gallery' },
    { label: 'Tentang Kami', value: stats.portfolio, icon: <Users size={24} />, color: '#10b981', link: '/admin/about' },
    { label: 'Artikel Terbit', value: stats.articles, icon: <FileText size={24} />, color: '#f59e0b', link: '/admin/articles' },
    { label: 'Loker Aktif', value: stats.loker_active, icon: <Briefcase size={24} />, color: '#ec4899', link: '/admin/loker' },
    { label: 'Pelamar Kerja', value: stats.applicants, icon: <UserPlus size={24} />, color: '#8b5cf6', link: '/admin/applicants' },
  ];

  return (
    <DashboardLayout>
      <div style={styles.heroWrapper}>
          {/* Background Decorations */}
          <Scissors size={120} style={styles.bgIcon1} />
          <Shirt size={140} style={styles.bgIcon2} />
          <Ruler size={80} style={styles.bgIcon3} />

          <div style={styles.heroContent}>
            <h1 style={styles.greeting}>Selamat {greeting}, Kang Iman..</h1>
            
            <div style={styles.quoteContainer}>
                <Quote size={24} style={styles.quoteIcon} />
                {quotes.length > 0 ? (
                    <div style={styles.quoteText}>
                        "{quotes[currentQuoteIndex]}"
                    </div>
                ) : (
                    <div style={styles.quoteText}>Siap untuk hari produktif ini?</div>
                )}
            </div>
          </div>
      </div>

      <h3 style={styles.sectionTitle}>Statistik Website</h3>
      <div style={styles.grid}>
        {statCards.map((stat, index) => (
          <Link to={stat.link} key={index} style={styles.card} onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
          }} onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.05)';
          }}>
            <div style={styles.iconBox(stat.color)}>
              {stat.icon}
            </div>
            <div>
              <div style={styles.label}>{stat.label}</div>
              <div style={styles.value}>{loading ? '...' : stat.value}</div>
            </div>
          </Link>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', padding: '0 1rem', marginBottom: '2rem' }}>
        
        {/* Latest Applicants Section */}
        <div style={{...styles.card, flexDirection: 'column', alignItems: 'stretch', padding: '0', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#fff' }}>
                <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <UserPlus size={18} color="#6366f1" />
                    Pelamar Terbaru
                </h4>
                <Link to="/admin/applicants" style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6366f1', textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                    Lihat Semua <ChevronRight size={14} />
                </Link>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                {recentApplicants.length === 0 ? (
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.9rem' }}>Belum ada pelamar baru.</div>
                ) : (
                    recentApplicants.map((app, i) => (
                        <div key={i} style={{ 
                            padding: '1rem 1.5rem', 
                            borderBottom: i !== recentApplicants.length - 1 ? '1px solid #f8fafc' : 'none',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            transition: 'background 0.2s',
                            cursor: 'pointer'
                        }} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}>
                            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                                <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '50%', backgroundColor: '#e0e7ff', color: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '0.9rem' }}>
                                    {app.name.charAt(0)}
                                </div>
                                <div>
                                    <div style={{ fontWeight: '600', color: '#334155', fontSize: '0.95rem' }}>{app.name}</div>
                                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Melamar: {app.job_title}</div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', color: '#94a3b8' }}>
                                <Clock size={12} />
                                {new Date(app.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>

        {/* Control Panel Info (Moved here side-by-side or stacked) */}
        <div style={{...styles.card, flexDirection: 'column', alignItems: 'flex-start', background: '#f8fafc', border: '1px dashed #cbd5e1', height: 'fit-content' }}>
            <h3 style={{margin: 0, fontSize: '1.1rem', color: '#334155'}}>Panel Kontrol</h3>
            <p style={{color: '#64748b', margin: 0, lineHeight: '1.6'}}>
            Gunakan menu di sidebar sebelah kiri untuk mengelola konten website. Pastikan data foto dan artikel selalu update untuk performa website yang maksimal.
            </p>
        </div>

      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
